import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, RefreshCw, ExternalLink, ShieldCheck, Gift, AlertTriangle, FlaskConical, Globe, X, Clock } from "lucide-react";
import { api, CURRENCY_META, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NETWORKS = [
  { key: "mainnet", label: "Mainnet", icon: Globe, hint: "Real funds · live chains" },
  { key: "testnet", label: "Testnet", icon: FlaskConical, hint: "Zero value · for testing" },
];
const COLOR = (c) => CURRENCY_META[c]?.color || "#FFC107";

const copyText = async (text, msg) => {
  await navigator.clipboard.writeText(text);
  toast.success(msg);
};

const Notes = ({ info }) => (
  <ul className="text-xs text-zinc-500 space-y-1.5">
    <li>Send only <span className="text-white">{info.network_label}</span> {info.currency} to this address — coins sent on any other network will be lost.</li>
    {info.token_contract && <li>Token contract: <span className="font-mono-num text-zinc-300 break-all" data-testid="deposit-token-contract">{info.token_contract}</span></li>}
    <li>Credited automatically after {info.min_confirmations} confirmation (polled every 15 minutes, or press “Check for deposits”).</li>
    <li className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> Watch-only: the server holds no private keys.</li>
  </ul>
);

const AddressCard = ({ info, isAdmin }) => (
  <div className="panel p-6 flex flex-col md:flex-row gap-8 items-start fade-up" data-testid="deposit-address-card">
    <div className="p-3 rounded-xl bg-white shrink-0" data-testid="deposit-qr"><QRCodeSVG value={info.address} size={168} /></div>
    <div className="flex-1 min-w-0 flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="chip w-fit" data-testid="deposit-network">{info.network_label}</div>
        <div className={`chip w-fit ${info.mainnet ? "text-emerald-400 border-emerald-500/30" : "text-zinc-400"}`} data-testid="deposit-network-kind">{info.mainnet ? "Mainnet" : "Testnet"}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your {info.currency} deposit address</div>
        <div className="panel px-4 py-3 flex items-center gap-3" style={{ background: "#0F0F11" }}>
          <span className="font-mono-num text-sm break-all flex-1" data-testid="deposit-address">{info.address}</span>
          <button onClick={() => copyText(info.address, "Address copied")} className="btn-ghost rounded-lg p-2" data-testid="deposit-copy-btn"><Copy size={14} /></button>
        </div>
      </div>
      {info.demo_keys && isAdmin && (
        <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="deposit-demo-keys-warning">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>Admin notice: addresses derive from placeholder xpubs. Replace <code>BTC_MAINNET_ZPUB</code> / <code>ETH_MAINNET_XPUB</code> in the backend .env before accepting real funds.</span>
        </div>
      )}
      <Notes info={info} />
    </div>
  </div>
);

const SharedDeposit = ({ info, coin, onCreated }) => {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/deposit/intent", { currency: coin, amount: Number(amount) });
      onCreated(data);
      setAmount("");
      toast.success("Deposit instructions ready — send the exact amount");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create deposit");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={create} className="panel p-6 flex flex-col gap-5 fade-up" data-testid="deposit-shared-form">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="chip w-fit" data-testid="deposit-network">{info.network_label}</div>
        <div className="chip w-fit text-emerald-400 border-emerald-500/30" data-testid="deposit-network-kind">Mainnet</div>
        <div className="chip w-fit text-cyan-300 border-cyan-500/30">Shared vault address</div>
      </div>
      <div className="text-sm text-zinc-400">
        Mainnet {coin} deposits go to the FMI vault address. We attribute your payment by its <span className="text-white">exact amount</span>, so enter how much you want to deposit and send precisely the amount shown.
      </div>
      <label className="flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">Amount to deposit</span>
        <div className="panel flex items-center gap-2 px-4 py-3" style={{ background: "#0F0F11" }}>
          <input type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="0.00"
            className="flex-1 bg-transparent outline-none font-mono-num text-right text-lg" data-testid="deposit-amount-input" />
          <span className="text-xs text-zinc-500 font-mono-num">{coin}</span>
        </div>
      </label>
      <button type="submit" disabled={busy} className="btn-gold rounded-xl py-3.5 font-display font-bold tracking-wide" data-testid="deposit-intent-submit">
        {busy ? "Preparing…" : "Get deposit instructions"}
      </button>
      <Notes info={{ ...info, currency: coin }} />
    </form>
  );
};

const IntentCard = ({ intent, onCancel }) => {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(intent.expires_at) - Date.now();
      setLeft(ms <= 0 ? "expired" : `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m left`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [intent.expires_at]);
  return (
    <div className="panel p-6 flex flex-col md:flex-row gap-8 items-start fade-up border-amber-500/30" data-testid="deposit-intent-card">
      <div className="p-3 rounded-xl bg-white shrink-0" data-testid="deposit-intent-qr"><QRCodeSVG value={intent.uri} size={168} /></div>
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="chip" style={{ color: COLOR(intent.currency), borderColor: `${COLOR(intent.currency)}55` }}>{intent.currency} · {intent.network_label || "Mainnet"}</span>
          <span className="chip text-amber-400 border-amber-500/30 flex items-center gap-1" data-testid="deposit-intent-expiry"><Clock size={11} /> {left}</span>
          <button onClick={() => onCancel(intent.id)} className="ml-auto btn-ghost rounded-lg p-1.5 text-zinc-500 hover:text-rose-400" title="Cancel these instructions" data-testid="deposit-intent-cancel"><X size={14} /></button>
        </div>
        <div className="text-[11px] text-zinc-500 -mt-2">Status: <span className="text-amber-400 uppercase">{intent.status}</span> · we'll credit you automatically once the transfer confirms.</div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Send exactly this amount</div>
          <div className="panel px-4 py-3 flex items-center gap-3 border-amber-500/40" style={{ background: "#0F0F11" }}>
            <span className="font-mono-num text-xl font-bold flex-1" data-testid="deposit-intent-amount">{intent.exact_amount} <span className="text-sm text-zinc-500">{intent.currency}</span></span>
            <button onClick={() => copyText(intent.exact_amount, "Amount copied")} className="btn-ghost rounded-lg p-2" data-testid="deposit-intent-copy-amount"><Copy size={14} /></button>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">To this address</div>
          <div className="panel px-4 py-3 flex items-center gap-3" style={{ background: "#0F0F11" }}>
            <span className="font-mono-num text-sm break-all flex-1" data-testid="deposit-address">{intent.address}</span>
            <button onClick={() => copyText(intent.address, "Address copied")} className="btn-ghost rounded-lg p-2" data-testid="deposit-copy-btn"><Copy size={14} /></button>
          </div>
        </div>
        <div className="text-xs text-zinc-500">The last digits of the amount identify your deposit. A different amount can't be matched automatically and will need operator review. Instructions are valid for 24 hours.</div>
      </div>
    </div>
  );
};

const STATUS_STYLE = { open: "text-amber-400 border-amber-500/30", credited: "text-emerald-400 border-emerald-500/30", cancelled: "text-zinc-500", expired: "text-zinc-500" };

const Intents = ({ rows, onCancel, onOpen }) => (
  <div className="panel overflow-hidden" data-testid="deposit-intents">
    {rows.map((r) => {
      const st = r.expired ? "expired" : r.status;
      return (
        <div key={r.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/[0.04] text-sm hover:bg-white/[0.03] cursor-pointer" onClick={() => onOpen(r)} data-testid={`deposit-intent-row-${r.id}`} style={{ transition: "background-color 140ms ease" }}>
          <span className="w-2 h-2 rounded-full" style={{ background: COLOR(r.currency) }} />
          <span className="font-mono-num flex-1">{r.exact_amount} {r.currency} <span className="text-[10px] text-zinc-500 uppercase ml-2">tap for QR</span></span>
          <span className="text-[11px] text-zinc-500 font-mono-num hidden sm:block">{new Date(r.created_at).toLocaleString()}</span>
          <span className={`chip uppercase tracking-wider text-[10px] ${STATUS_STYLE[st] || ""}`}>{st}</span>
          {r.explorer_url && <a href={r.explorer_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-zinc-500 hover:text-cyan-300"><ExternalLink size={14} /></a>}
          {r.status === "open" && !r.expired && <button onClick={(e) => { e.stopPropagation(); onCancel(r.id); }} className="btn-ghost rounded-lg p-1.5 text-rose-400" data-testid={`deposit-intent-cancel-${r.id}`}><X size={13} /></button>}
        </div>
      );
    })}
  </div>
);

const History = ({ rows }) => (
  <div className="panel overflow-hidden" data-testid="deposit-history">
    {rows.length === 0 ? (
      <div className="p-8 text-center text-sm text-zinc-500" data-testid="deposit-history-empty">No on-chain deposits yet.</div>
    ) : (
      rows.map((d) => (
        <div key={d.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/[0.04] text-sm" data-testid={`deposit-row-${d.id}`}>
          <span className="w-2 h-2 rounded-full" style={{ background: COLOR(d.currency) }} />
          <span className="font-mono-num text-emerald-400">+{formatAmount(d.amount, d.currency)} {d.currency}</span>
          <span className="chip text-[9px] py-0 uppercase">{d.network}</span>
          <span className="text-zinc-500 text-xs flex-1 truncate font-mono-num">{d.txid}</span>
          {d.bonus_fmi && <span className="text-[11px] text-amber-400 font-mono-num">+{formatAmount(d.bonus_fmi, "FMI")} bonus</span>}
          {d.explorer_url && <a href={d.explorer_url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-cyan-300"><ExternalLink size={14} /></a>}
        </div>
      ))
    )}
  </div>
);

export default function Deposit() {
  const { user, setUser } = useAuth();
  const [network, setNetwork] = useState("mainnet");
  const [coin, setCoin] = useState("BTC");
  const [cfg, setCfg] = useState(null);
  const [info, setInfo] = useState(null);
  const [intents, setIntents] = useState([]);
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [checking, setChecking] = useState(false);
  const [bonus, setBonus] = useState(null);

  const loadHistory = () => api.get("/deposit/history").then((r) => setRows(r.data));
  const loadIntents = () => api.get("/deposit/intents/me").then((r) => setIntents(r.data)).catch(() => {});
  const coins = cfg ? Object.keys(cfg.currencies).filter((c) => cfg.currencies[c].networks.includes(network)) : [];

  useEffect(() => {
    api.get("/bonus/deposit").then((r) => setBonus(r.data)).catch(() => {});
    api.get("/deposit/networks").then((r) => setCfg(r.data)).catch(() => {});
    loadHistory();
    loadIntents();
  }, []);

  useEffect(() => {
    if (cfg && !coins.includes(coin)) setCoin(coins[0]);
  }, [network, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cfg || !coins.includes(coin)) return;
    setInfo(null);
    setActive(null);
    let stale = false;
    api.get(`/deposit/address/${coin}?network=${network}`).then((r) => { if (!stale) setInfo(r.data); }).catch(() => toast.error("Could not load address"));
    return () => { stale = true; };
  }, [coin, network, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/deposit/check");
      setUser((u) => ({ ...u, balances: data.balances }));
      if (data.credited > 0) toast.success(`${data.credited} deposit(s) credited`);
      else toast("No new confirmed deposits yet");
      loadHistory();
      loadIntents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const cancelIntent = async (id) => {
    try {
      await api.post(`/deposit/intents/${id}/cancel`);
      if (active?.id === id) setActive(null);
      loadIntents();
      toast("Deposit instructions cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const openIntents = intents.filter((i) => i.status === "open" && !i.expired);

  return (
    <div className="flex flex-col gap-8 max-w-4xl" data-testid="deposit-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Deposit</h1>
        <p className="text-zinc-500 text-sm mt-1">BTC, ETH, USDT, SOL and FMI (ERC-20) on mainnet · BTC and ETH on testnet. Watch-only, credited after 1 confirmation.</p>
      </div>
      {bonus && (
        <div className="panel p-5 flex flex-col md:flex-row md:items-center gap-4 relative overflow-hidden" data-testid="deposit-bonus-banner">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(500px 160px at 0% 0%, rgba(255,193,7,0.14), transparent)" }} />
          <Gift size={28} className="text-amber-400 shrink-0 relative" />
          <div className="relative flex-1">
            {bonus.claimed ? (
              <>
                <div className="font-display font-black text-lg">First deposit bonus claimed</div>
                <div className="text-sm text-zinc-500">You received <span className="text-emerald-400 font-mono-num" data-testid="deposit-bonus-claimed">+{formatAmount(bonus.claimed.amount, "FMI")} FMI</span> on your {bonus.claimed.deposit} {bonus.claimed.currency} deposit.</div>
              </>
            ) : (
              <>
                <div className="font-display font-black text-lg"><span className="gold-text">{bonus.percent}% match</span> on your first deposit</div>
                <div className="text-sm text-zinc-500">
                  Paid instantly in FMI, up to <span className="text-white font-mono-num">{formatAmount(bonus.cap_fmi, "FMI")} FMI</span>. Rates: 1 BTC = {formatAmount(bonus.rates.BTC, "FMI")} · 1 ETH = {formatAmount(bonus.rates.ETH, "FMI")} · 1 USDT = {formatAmount(bonus.rates.USDT, "FMI")}{bonus.rates.SOL ? ` · 1 SOL = ${formatAmount(bonus.rates.SOL, "FMI")}` : ""} FMI.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2" data-testid="deposit-network-tabs">
        {NETWORKS.map(({ key, label, icon: Icon, hint }) => (
          <button key={key} onClick={() => setNetwork(key)} data-testid={`deposit-network-${key}`}
            className={`panel panel-hover px-4 py-3 flex items-center gap-3 text-left ${network === key ? "border-amber-500/50 bg-amber-500/5" : ""}`}>
            <Icon size={18} className={network === key ? "text-amber-400" : "text-zinc-500"} />
            <div>
              <div className="text-sm font-semibold">{label}</div>
              <div className="text-[11px] text-zinc-500">{hint}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {coins.map((c) => (
          <button key={c} onClick={() => setCoin(c)} className={`px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 ${coin === c ? "btn-cyan" : "btn-ghost"}`} data-testid={`deposit-coin-${c}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: COLOR(c) }} /> {c}
          </button>
        ))}
        <button onClick={check} disabled={checking} className="btn-gold rounded-lg px-4 py-2.5 text-sm ml-auto flex items-center gap-2" data-testid="deposit-check-btn">
          <RefreshCw size={14} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "Check for deposits"}
        </button>
      </div>

      {!info ? (
        <div className="panel p-10 text-zinc-500 text-sm">Loading…</div>
      ) : info.mode === "shared" ? (
        active ? <IntentCard intent={active} onCancel={cancelIntent} /> : <SharedDeposit info={info} coin={coin} onCreated={(i) => { setActive(i); loadIntents(); }} />
      ) : (
        <AddressCard info={info} isAdmin={user?.role === "admin"} />
      )}

      {openIntents.length > 0 && !active && (
        <section>
          <h2 className="font-display text-lg font-black tracking-tighter mb-3">Pending deposit instructions</h2>
          <Intents rows={openIntents} onCancel={cancelIntent} onOpen={(r) => { setActive(r); }} />
        </section>
      )}

      <section>
        <h2 className="font-display text-lg font-black tracking-tighter mb-3">Deposit history</h2>
        <History rows={rows} />
      </section>
    </div>
  );
}
